// What happened when a prop account hit its max loss (BlownCard.js on Home): the day and the
// trade that crossed the line, the last trading days, the likely cause with its proof from the
// trades (the journal's mistake checks, behaviorDetect.js, or a slow bleed or one big day), one
// rule to take from it, and how it ended for the archive (AccountEnded.js).
import { accountStatus } from './balance';
import { PATTERNS, detectBehavior, ruleFor } from './behaviorDetect';
import { netPnl, tradingDayKey } from './pnl';
import { translateNow as tn } from './langState';

const WINDOW_DAYS = 5; // the blown day and the trading days before it that are looked at

export function blownReport(account, trades, { allTrades = trades, accounts = [account], notes = {} } = {}) {
  const status = accountStatus(account, trades);
  if (!status || status.risk !== 'blown') return null;
  const sorted = trades.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  const dayOf = (t) => tradingDayKey(t.date);
  const days = [...new Set(sorted.map(dayOf))];

  // The first trade after which the account was at or under its max loss level.
  let crossing = null;
  for (let i = 0; i < sorted.length; i += 1) {
    const s = accountStatus(account, sorted.slice(0, i + 1));
    if (s && s.risk === 'blown') { crossing = sorted[i]; break; }
  }
  const blownDay = crossing ? dayOf(crossing) : days[days.length - 1];
  const window = days.slice(Math.max(0, days.indexOf(blownDay) - (WINDOW_DAYS - 1)), days.indexOf(blownDay) + 1);
  const inWindow = sorted.filter((t) => window.includes(dayOf(t)));
  const dayNet = Object.fromEntries(window.map((d) => [d, inWindow.filter((t) => dayOf(t) === d).reduce((s, t) => s + netPnl(t), 0)]));
  const lastDays = window.slice(-3).map((d) => ({ day: d, net: dayNet[d], trades: inWindow.filter((t) => dayOf(t) === d).length }));
  const windowLoss = Math.min(0, inWindow.reduce((s, t) => s + netPnl(t), 0));
  const mll = account.maxLossLimit || 0;

  // The journal's mistake checks on these days: how many trades and what they cost.
  const found = detectBehavior(allTrades, { accounts, notes });
  const byKind = {};
  for (const t of inWindow) {
    for (const f of found.flags.get(t._id) || []) {
      const k = (byKind[f.kind] = byKind[f.kind] || { kind: f.kind, trades: 0, cost: 0 });
      k.trades += 1;
      k.cost += netPnl(t);
    }
  }
  const causes = Object.values(byKind)
    .filter((c) => c.cost < 0 && !PATTERNS.find((p) => p.kind === c.kind).costless)
    .map((c) => ({ ...c, title: tn(PATTERNS.find((p) => p.kind === c.kind).name), rule: ruleFor(c.kind, found.baseline) }));

  // No single mistake: a big losing day, or a slow bleed over several days.
  const blownDayLoss = dayNet[blownDay] || 0;
  if (mll && blownDayLoss <= -mll * 0.5) {
    causes.push({ kind: 'bigDay', trades: inWindow.filter((t) => dayOf(t) === blownDay).length, cost: blownDayLoss, title: tn('One big losing day'), rule: tn('When I’m down {amount} on the day, I stop.', { amount: `$${Math.round(mll * 0.25)}` }) });
  }
  const losingDays = window.filter((d) => dayNet[d] < 0).length;
  if (losingDays >= 3 && blownDayLoss > -mll * 0.5) {
    causes.push({ kind: 'bleed', trades: inWindow.length, cost: windowLoss, losingDays, title: tn('A slow bleed: losing day after losing day'), rule: tn('After 2 losing days in a row, I trade half size until I have a green day.') });
  }
  // A specific mistake explains more than "a big day" or "a bleed", so mistakes come first, costliest first.
  const general = new Set(['bigDay', 'bleed']);
  causes.sort((a, b) => Number(general.has(a.kind)) - Number(general.has(b.kind)) || a.cost - b.cost);

  // How it ended, for the archive: the daily loss limit, a trailing drawdown, or the max loss.
  const endCause = account.dailyLossLimit && blownDayLoss <= -account.dailyLossLimit ? 'daily_loss'
    : account.drawdownType && account.drawdownType !== 'static' ? 'trailing_drawdown' : 'max_loss';

  return {
    blownDay, crossing, lastDays, causes, top: causes[0] || null, endCause,
    windowDays: window.length, windowLoss, losingDays, floor: status.floor, balance: status.balance,
    rule: causes[0] ? causes[0].rule : tn('After 2 losses, the day is over.'),
    symbol: crossing ? crossing.symbol : sorted.length ? sorted[sorted.length - 1].symbol : '',
  };
}

// What the trader did that ended an account (Account.behaviorCause on the server), with names.
export const BEHAVIOR_CAUSES = ['revenge', 'sizeUp', 'streak', 'pastLimit', 'overtrading', 'unplanned', 'bigDay', 'bleed', 'other'];
export const BEHAVIOR_CAUSE_NAMES = {
  ...Object.fromEntries(PATTERNS.map((p) => [p.kind, p.name])),
  bigDay: 'One big losing day', bleed: 'A slow bleed: losing day after losing day', other: 'Something else',
};

// The account as it is when it's archived (Account.endSnapshot): how long it lasted, its trades,
// win rate, average win and loss, how close it got to the target, and the mistakes the journal
// found in it with what they cost. Kept as it was, even if the rules or the trades change later.
export function endSnapshot(account, trades, { allTrades = trades, accounts = [account], notes = {} } = {}) {
  if (!trades.length) return null;
  const sorted = trades.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  const nets = sorted.map(netPnl);
  const wins = nets.filter((x) => x > 0);
  const losses = nets.filter((x) => x < 0);
  const sum = (a) => a.reduce((s, x) => s + x, 0);
  const round = (x) => Math.round(x * 100) / 100;
  const first = tradingDayKey(sorted[0].date);
  const last = tradingDayKey(sorted[sorted.length - 1].date);
  const status = accountStatus(account, sorted);
  const found = detectBehavior(allTrades, { accounts, notes });
  const ids = new Set(sorted.map((t) => t._id));
  const mistakes = {};
  for (const [id, flags] of found.flags) {
    if (!ids.has(id)) continue;
    const t = sorted.find((x) => x._id === id);
    for (const f of flags) {
      const m = (mistakes[f.kind] = mistakes[f.kind] || { kind: f.kind, trades: 0, cost: 0 });
      m.trades += 1;
      m.cost = round(m.cost + netPnl(t));
    }
  }
  const report = status && status.risk === 'blown' ? blownReport(account, sorted, { allTrades, accounts, notes }) : null;
  return {
    first, last,
    days: Math.round((new Date(`${last}T12:00:00`) - new Date(`${first}T12:00:00`)) / 86400000) + 1,
    tradingDays: new Set(sorted.map((t) => tradingDayKey(t.date))).size,
    trades: sorted.length,
    net: round(sum(nets)),
    winRate: round(wins.length / sorted.length),
    avgWin: wins.length ? round(sum(wins) / wins.length) : null,
    avgLoss: losses.length ? round(sum(losses) / losses.length) : null,
    progress: status && status.progress !== undefined ? round(status.progress) : null,
    mistakes: Object.values(mistakes).sort((a, b) => a.cost - b.cost).slice(0, 6),
    cause: report && report.top ? report.top.kind : null,
    blownDay: report ? report.blownDay : null,
  };
}
